import RestX from 'highered-utils/restx';
import moment from 'moment';
import { VFairWebinarShallow } from '~/services/types/vfair';
import { MessageModule, MessageState } from '~/store/customModules/messageModule';
import { WebinarMessageModule } from '~/store/customModules/webinarMessageModule';

const LS_WEBINAR_MESSAGE_IGNORE_KEY = 'highered-webinar-message-ignore';

/** Each value is the start time of the webinar, for cleaning up expired webinars */
interface LSWebinarMessageIgnoreConfig {
    [id: string]: string;
}

export interface WebinarMessageServiceConfig {
    /**
     * How long before a webinar a message should be shown, in milliseconds
     * @default 5000 * 60 // 5 minutes
     */
    delta?: number;
    /**
     * How often we should check for upcoming events, in milliseconds
     * @default 1000 * 30 // 30 seconds
     */
    interval?: number;
    /**
     * Special interval for first check, in milliseconds
     * @default 5000 // 5 seconds
     */
    initialInterval?: number;
}

class WebinarMessageService {
    private timeoutId: number = -1;
    private currentNotificationNumber = 0;

    constructor(
        private _config: WebinarMessageServiceConfig = {
            delta: 5000 * 60,
            interval: 1000 * 30,
            initialInterval: 5000,
        }
    ) {}

    get config() {
        return this._config;
    }
    set config(config: WebinarMessageServiceConfig) {
        this._config = config;
    }

    /** Get webinar message store */
    private getModule() {
        return (RestX.getInterfaces() as any).webinarMessageModule as WebinarMessageModule;
    }
    /** Get regular message store */
    private getMessageModule() {
        return (RestX.getInterfaces() as any).messageModule as MessageModule;
    }

    /** Get webinars to be ignored from localStorage */
    private getIgnore() {
        const jsonString = window.localStorage.getItem(LS_WEBINAR_MESSAGE_IGNORE_KEY);
        if (!!jsonString) {
            let json: LSWebinarMessageIgnoreConfig = {};
            try {
                json = JSON.parse(jsonString);
                return json;
            } catch {
                console.error(`Invalid JSON in localStorage: ${LS_WEBINAR_MESSAGE_IGNORE_KEY}`);
            }
        }
        return {};
    }
    /**
     * Update entire ignore object in localStorage
     * @param config IgnoreConfig to store
     */
    private setIgnore(config: LSWebinarMessageIgnoreConfig) {
        window.localStorage.setItem(LS_WEBINAR_MESSAGE_IGNORE_KEY, JSON.stringify(config));
    }
    /**
     * Add webinar to ignore config in localStorage
     * @param id ID of webinar to add to ignore config
     * @param startTime Start time of webinar
     */
    private addIgnore(id: string, startTime: string) {
        let ignore = this.getIgnore();
        ignore = {
            ...ignore,
            [id]: startTime,
        };

        const jsonString = JSON.stringify(ignore);
        window.localStorage.setItem(LS_WEBINAR_MESSAGE_IGNORE_KEY, jsonString);
    }
    /**
     * Check if webinar exists in localStorage ignore config
     * @param id Id of webinar to look for
     * @returns Whether webinar exists in ignore config
     */
    private ignoreContains(id: string) {
        const ignore = this.getIgnore();
        return ignore.hasOwnProperty(id);
    }

    /**
     * Update document title notification
     * @param increase Whether to add or subtract a number from title notification
     */
    private updateDocumentTitle(increase: boolean) {
        const { title } = document;
        const baseTitle = title.replace(/^\(\d+\) /, '');

        this.currentNotificationNumber += increase ? 1 : -1;
        if (this.currentNotificationNumber === 0) {
            document.title = baseTitle;
            return;
        }

        document.title = `(${this.currentNotificationNumber}) ${baseTitle}`;
    }

    /**
     * Send a notification for an upcoming webinar
     * @param webinar The webinar to notify about
     */
    private sendMessage(webinar: VFairWebinarShallow) {
        if (!webinar.startTime) return;

        const module = this.getMessageModule();
        const fromNow = moment(webinar.startTime).fromNow(true);
        const message = `Webinar starts in ${fromNow}`;

        this.updateDocumentTitle(true);

        module.addNewMessage({
            title: webinar.title,
            message,
            persistent: true,
            state: MessageState.WEBINAR,
            link: !!webinar.vFairId
                ? {
                      href: `/vevent/${webinar.vFairId}/webinars/${webinar.id}`,
                      text: 'Go to webinar',
                  }
                : undefined,
            playSound: true,
            onClose: () => {
                if (!webinar.startTime) return;
                this.updateDocumentTitle(false);
                this.addIgnore(webinar.id, webinar.startTime);
            },
        });
    }
    /** Check for upcoming webinars, and display notifications */
    private check() {
        const upcoming = this.getUpcomingWebinars(this.config.delta);
        for (const webinar of upcoming) {
            if (!webinar.startTime || this.ignoreContains(webinar.id)) continue;

            this.sendMessage(webinar);
            this.addIgnore(webinar.id, webinar.startTime);
        }

        this.clean();
    }
    /** Clean out expired webinars from ignore config */
    private clean() {
        const now = new Date().getTime();
        const ignore = this.getIgnore();

        for (const [id, startTime] of Object.entries(ignore)) {
            const start = new Date(startTime).getTime();
            if (isNaN(start) || start < now) delete ignore[id];
        }
        this.setIgnore(ignore);
    }

    /** Get all webinars in store */
    public getWebinars() {
        return this.getModule().getWebinars();
    }
    /** Get all upcoming webinars in store */
    public getUpcomingWebinars(delta?: number) {
        return this.getModule().getUpcomingWebinars(delta || this.config.delta);
    }

    /**
     * Add webinars to store
     * @param webinars Webinars to add
     */
    public addWebinars(webinars: VFairWebinarShallow[]) {
        const ignore = this.getIgnore();
        if (ignore.length) webinars = webinars.filter(wb => !this.ignoreContains(wb.id));

        this.getModule().addWebinars(webinars);
    }
    /**
     * Delete a webinar from store, and add to ignore config in localStorage
     * @param webinar Webinar to delete
     */
    public deleteWebinar(webinar: Pick<VFairWebinarShallow, 'id' | 'startTime'>) {
        if (!webinar.startTime) return;
        this.addIgnore(webinar.id, webinar.startTime);

        this.getModule().deleteWebinar(webinar.id);
    }

    /** Start checking interval */
    public start() {
        const handler = () => {
            this.check();
            this.timeoutId = window.setTimeout(handler, this.config.interval);
        };
        this.timeoutId = window.setTimeout(handler, this.config.initialInterval);
    }
    /** Stop checking interval */
    public stop() {
        window.clearTimeout(this.timeoutId);
    }
}

export default WebinarMessageService;
